<template>
  <div class="evidence-list">
    <div class="evidence-header">
      <span class="evidence-eyebrow">Records cited</span>
      <span class="evidence-count">{{ evidence.length }}</span>
    </div>
    <ul class="evidence-items">
      <li
        v-for="(item, idx) in evidence"
        :key="item.id || idx"
        class="evidence-item"
        :class="[`type-${item.type}`, statusClass(item)]"
      >
        <span class="evidence-index">[{{ idx + 1 }}]</span>
        <div class="evidence-body">
          <div class="evidence-title-row">
            <span class="evidence-title">{{ headline(item) }}</span>
            <span v-if="badge(item)" class="evidence-badge" :class="statusClass(item)">
              {{ badge(item) }}
            </span>
          </div>
          <p v-if="subline(item)" class="evidence-subline">{{ subline(item) }}</p>
          <p v-if="item.snippet" class="evidence-snippet">{{ item.snippet }}</p>
          <div class="evidence-meta">
            <span class="evidence-kind">{{ labelFor(item.type) }}</span>
            <button
              v-if="item.document_id"
              type="button"
              class="evidence-action"
              @click="$emit('open-document', item.document_id)"
            >
              Open certificate
            </button>
            <button
              v-if="item.subject_id"
              type="button"
              class="evidence-action"
              @click="$emit('open-snapshot', item.subject_id)"
            >
              Open record
            </button>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'ComplianceEvidenceList',
  props: {
    evidence: { type: Array, required: true },
  },
  emits: ['open-document', 'open-snapshot'],
  setup() {
    const labelFor = (type) => ({
      snapshot: 'Posture',
      snapshot_summary: 'Summary',
      expiration: 'Expiration',
      check_run: 'Certificate review',
      document: 'Certificate',
      rule: 'Requirement',
      notification: 'Notification',
      memory: 'Earlier note',
      exception: 'Approved exception',
    }[type] || 'Record')

    const headline = (item) => {
      if (item.type === 'document' || item.type === 'check_run' || item.type === 'rule' || item.type === 'notification' || item.type === 'memory') {
        return item.title || item.entity_name || 'Record'
      }
      return item.entity_name || item.title || 'Record'
    }

    const subline = (item) => {
      if (!item.entity_name) return ''
      if (headline(item) === item.entity_name) return ''
      return item.entity_name
    }

    const badge = (item) => {
      if (item.current_score != null && item.status_label) {
        return `${item.current_score}% ${item.status_label}`
      }
      if (item.type === 'expiration' && item.soonest_days_until != null) {
        const days = Number(item.soonest_days_until)
        if (Number.isNaN(days)) return ''
        if (days < 0) return 'Expired'
        return `${days} days`
      }
      return ''
    }

    const statusClass = (item) => {
      const label = String(item.status_label || badge(item) || '').toLowerCase()
      if (label.includes('critical') || label.includes('expired')) return 'status-critical'
      if (label.includes('risk') || label.includes('attention')) return 'status-warning'
      if (label.includes('compliant') || label.includes('excellent') || label.includes('good')) {
        return 'status-good'
      }
      return ''
    }

    return { labelFor, headline, subline, badge, statusClass }
  },
}
</script>

<style scoped>
.evidence-list {
  margin-top: 12px;
  border: 1px solid #dbe3ee;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

.evidence-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.55rem 0.85rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.evidence-eyebrow {
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.evidence-count {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
}

.evidence-items {
  list-style: none;
  margin: 0;
  padding: 0;
}

.evidence-item {
  display: flex;
  gap: 0.65rem;
  padding: 0.75rem 0.85rem;
  border-bottom: 1px solid #e2e8f0;
  border-left: 3px solid #cbd5e1;
}

.evidence-item:last-child {
  border-bottom: none;
}

.evidence-item.status-critical {
  border-left-color: #dc2626;
}

.evidence-item.status-warning {
  border-left-color: #d97706;
}

.evidence-item.status-good {
  border-left-color: #059669;
}

.evidence-item.type-memory {
  background: #f8fafc;
}

.evidence-index {
  flex-shrink: 0;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  padding-top: 0.15rem;
}

.evidence-body {
  flex: 1;
  min-width: 0;
}

.evidence-title-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.evidence-title {
  font-size: 0.9rem;
  font-weight: 650;
  color: #0f172a;
}

.evidence-badge {
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #475569;
}

.evidence-badge.status-critical { color: #b91c1c; }
.evidence-badge.status-warning { color: #b45309; }
.evidence-badge.status-good { color: #047857; }

.evidence-subline {
  margin: 0.15rem 0 0;
  color: #475569;
  font-size: 0.8rem;
}

.evidence-snippet {
  margin: 0.3rem 0 0;
  color: #334155;
  font-size: 0.8rem;
  white-space: pre-line;
}

.evidence-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.4rem;
}

.evidence-kind {
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.evidence-action {
  border: 0;
  background: transparent;
  color: #4f46e5;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0;
}

.evidence-action:hover {
  text-decoration: underline;
}
</style>
